import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  fetchRolePermissionsData,
  saveRolePermissions,
} from "store/features/company/roleManagement/rolePermissionActions";
import {
  selectRolePermissionRoles,
  selectRolePermissionPermissions,
} from "store/features/company/roleManagement/rolePermissionSelectors";
import permissionsSvg from "assets/folders/permissions.svg";
import usePermissions from "hooks/usePermissions";

const RolePermission = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const roles = useSelector(selectRolePermissionRoles) || [];
  const permissions = useSelector(selectRolePermissionPermissions) || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [modalPermissions, setModalPermissions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { hasPermission, hasAnyPermission } = usePermissions();

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    dispatch(fetchRolePermissionsData());
  }, [dispatch]);

  useEffect(() => {
    const map = {};
    roles.forEach((role) => {
      map[role.id] = role.permissions?.map((p) => p.id) || [];
    });
    setSelectedPermissions(map);
  }, [roles]);

  const filteredRoles = roles.filter((role) => {
    const matchesSearch =
      searchTerm === "" ||
      role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.code?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getTenantLabel = (tenantId) => {
    if (tenantId === null) return "Global";
    return `Tenant ${tenantId}`;
  };

  const groupedPermissions = permissions.reduce((acc, p) => {
    if (!acc[p.resource]) acc[p.resource] = [];
    acc[p.resource].push(p);
    return acc;
  }, {});

  const handleOpenModal = (role) => {
    setCurrentRole(role);
    setModalPermissions(selectedPermissions[role.id] || []);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setCurrentRole(null);
    setModalPermissions([]);
  };

  const handleToggleModalPermission = (permissionId) => {
    setModalPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSave = () => {
    if (currentRole) {
      dispatch(saveRolePermissions(currentRole.id, modalPermissions));
      setSelectedPermissions((prev) => ({
        ...prev,
        [currentRole.id]: modalPermissions,
      }));
    }
    handleCloseModal();
  };

  if (!hasPermission("role_permissions.view")) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={permissionsSvg}
              alt="Permissions"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Role Permissions
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                Manage permissions mapped to each role
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Search Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          placeholder="Search by role name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              bgcolor: "background.paper",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Roles Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 10px 25px -5px rgba(0,0,0,0.3)"
              : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
          border: `1px solid ${theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 800 }}>
            <TableHead
              sx={{
                "& th": {
                  backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                  ID / Tenant
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    paddingLeft: "70px",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                >
                  Role
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                >
                  Assigned Permissions
                </TableCell>
                {hasAnyPermission(["role_permissions.update", "role_permissions.delete"]) && (
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  >
                    Action
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <AdminIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
                    <Typography
                      sx={{
                        color: "text.secondary",
                      }}
                    >
                      No roles found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((role, index) => (
                  <TableRow
                    key={role.id}
                    component={motion.tr}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    sx={{
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(30,41,59,0.5)"
                            : "#f9fafb",
                      },
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography
                          sx={{
                            textAlign: "center",
                            fontFamily: "monospace",
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: "primary.main",
                          }}
                        >
                          {/* #{role.id} */}
                          #{index + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            color: "text.secondary",
                          }}
                        >
                          {/* Tenant: {role.tenant_id} */}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 28, color: "primary.main" }} />
                        </Box>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {role.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontFamily: "monospace",
                              color: "text.secondary",
                            }}
                          >
                            {role.code}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              fontFamily: "monospace",
                              color: "text.secondary",
                            }}
                          >
                            {role.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={getTenantLabel(role.tenant_id)}
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor:
                              role.tenant_id === null ? "#dbeafe" : "#fef3c7",
                            color:
                              role.tenant_id === null ? "#1e40af" : "#92400e",
                            fontWeight: 500,
                            fontSize: "0.65rem",
                            height: "20px",
                            ...(theme.palette.mode === "dark" && {
                              backgroundColor:
                                role.tenant_id === null
                                  ? "rgba(59,130,246,0.25)"
                                  : "rgba(245,158,11,0.25)",
                              color:
                                role.tenant_id === null ? "#93c5fd" : "#fde68a",
                            }),
                          }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${selectedPermissions[role.id]?.length || 0} / ${permissions.length}`}
                        size="small"
                        sx={{
                          backgroundColor: theme.palette.mode === "dark" ? "rgba(59, 130, 246, 0.1)" : "#eff6ff",
                          color: theme.palette.mode === "dark" ? "#60a5fa" : "#2563eb",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {hasPermission("role_permissions.update") && (
                        <Button
                          variant="contained"
                          startIcon={<SettingsIcon />}
                          onClick={() => handleOpenModal(role)}
                          sx={{
                            background:
                              theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                            color: "#ffffff",
                            borderRadius: "10px",
                            textTransform: "none",
                            px: 2,
                            py: 0.5,
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            boxShadow: "none",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              background:
                                theme.palette.mode === "dark"
                                  ? "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)"
                                  : "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                              boxShadow:
                                theme.palette.mode === "dark"
                                  ? "0 4px 12px rgba(59, 130, 246, 0.3)"
                                  : "0 4px 12px rgba(245, 158, 11, 0.3)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          Manage
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRoles.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Permissions Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
              }`,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Manage Permissions
            </Typography>
            {currentRole && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                Role: <Box component="span" sx={{ color: theme.palette.mode === "dark" ? "#fcd34d" : "#d97706", fontWeight: "bold" }}>{currentRole.name}</Box> ({currentRole.code})
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#f8fafc" }}>
          <Box sx={{ m: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Select the permissions to assign to this role. Grouped by resource.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                // Select or deselect all
                if (modalPermissions.length === permissions.length) {
                  setModalPermissions([]);
                } else {
                  setModalPermissions(permissions.map(p => p.id));
                }
              }}
              sx={{ borderRadius: "8px", textTransform: 'none' }}
            >
              {modalPermissions.length === permissions.length ? "Deselect All" : "Select All"}
            </Button>
          </Box>

          <Grid container spacing={3}>
            {Object.entries(groupedPermissions).map(([resource, perms]) => (
              <Grid item xs={12} sm={6} md={4} key={resource}>
                <Paper
                  elevation={0}
                  sx={{
                    // p: 2,
                    borderRadius: "12px",
                    bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                    border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"}`,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: `1px dashed ${theme.palette.mode === "dark" ? "#475569" : "#e5e7eb"}` }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        px: 2,
                        pt: 1,
                        fontWeight: 700,
                        color: theme.palette.mode === "dark" ? "#e2e8f0" : "#1f2937",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                      }}
                    >
                      {resource}
                    </Typography>
                    {/* Resource quick toggle */}
                    <Checkbox
                      sx={{
                        mt: 1,
                        pt: 0.5,
                      }}
                      size="small"
                      checked={perms.every(p => modalPermissions.includes(p.id))}
                      indeterminate={perms.some(p => modalPermissions.includes(p.id)) && !perms.every(p => modalPermissions.includes(p.id))}
                      onChange={() => {
                        const allSelected = perms.every(p => modalPermissions.includes(p.id));
                        if (allSelected) {
                          setModalPermissions(prev => prev.filter(id => !perms.find(p => p.id === id)));
                        } else {
                          const newIds = perms.map(p => p.id).filter(id => !modalPermissions.includes(id));
                          setModalPermissions(prev => [...prev, ...newIds]);
                        }
                      }}
                    />
                  </Box>
                  <FormGroup>
                    {perms.map((p) => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={modalPermissions.includes(p.id)}
                            onChange={() => handleToggleModalPermission(p.id)}
                            sx={{
                              ml: 2,
                              color: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                              "&.Mui-checked": {
                                color: "primary.main",
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: theme.palette.mode === "dark" ? "#cbd5e1" : "#4b5563" }}>
                            {p.action}
                          </Typography>
                        }
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2.5,
            borderTop: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#f3f4f6"
              }`,
            bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          }}
        >
          <Button
            onClick={handleCloseModal}
            sx={{
              color: "text.secondary",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "8px",
              textTransform: "none",
              px: 3,
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
              },
            }}
          >
            Save Permissions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolePermission;